import { isGeometryMultiPolygon, isGeometryPolygon, type GeoJsonData } from '../PcaMap';

interface FitBoundsToGeoJsonArgs {
  map: google.maps.Map;
  geoJsonData: GeoJsonData;
  onFitBoundsStart?: () => void;
  onFitBoundsEnd?: () => void;
  setCurrentMapBounds?: (mapBounds: google.maps.LatLngBounds | null) => void;
  currentMapBounds?: google.maps.LatLngBounds | null;
  shouldDelayFitBounds?: boolean;
}

export const fitBoundsToGeoJson = ({
  map,
  geoJsonData,
  onFitBoundsStart,
  onFitBoundsEnd,
  setCurrentMapBounds,
  currentMapBounds,
  shouldDelayFitBounds = false,
}: FitBoundsToGeoJsonArgs): void => {
  const featuresWithCoordinates = geoJsonData.features.filter((feature) =>
    Boolean(feature.geometry.coordinates),
  );
  if (!featuresWithCoordinates.length) return;
  const bounds = new google.maps.LatLngBounds();

  featuresWithCoordinates.forEach((feature) => {
    if (!feature.geometry.coordinates) return;
    if (isGeometryMultiPolygon(feature.geometry)) {
      /**
       * For type "MultiPolygon", the "coordinates" member is an array of Polygon coordinate arrays.
       * We only have MultiPolygon for school zone geojson.
       * @see https://datatracker.ietf.org/doc/html/rfc7946#section-3.1.7
       */
      feature.geometry.coordinates.forEach((polygonCoordinateArrays) => {
        polygonCoordinateArrays.forEach((polygonCoordinateArray) => {
          polygonCoordinateArray.forEach(([lng, lat]) => {
            if (!lat || !lng) return;
            bounds.extend({ lat, lng });
          });
        });
      });
    } else if (isGeometryPolygon(feature.geometry)) {
      /**
       * For type "Polygon", the "coordinates" is an array of LinearRing coordinate arrays.
       * @see https://datatracker.ietf.org/doc/html/rfc7946#section-3.1.6
       */
      feature.geometry.coordinates.forEach((polygonCoordinateArray) => {
        polygonCoordinateArray.forEach(([lng, lat]) => {
          if (!lat || !lng) return;
          bounds.extend({ lat, lng });
        });
      });
    }
  });

  // Fit bounds only if bounds have changed, otherwise when fitBounds is called with the same bounds, it will never trigger an idle event, so that the map never get unsuppressed
  if (currentMapBounds && currentMapBounds.equals(bounds)) return;

  setCurrentMapBounds?.(bounds);
  const fitBounds = () => {
    if (onFitBoundsStart) onFitBoundsStart();
    map.fitBounds(bounds);
    const fitBoundsFinishedListener = map.addListener('idle', () => {
      fitBoundsFinishedListener.remove();
      if (onFitBoundsEnd) onFitBoundsEnd();
    });
  };
  // Delay the fitBounds to prevent the map from fitbound before the map is fully extended, which will cause the map to zoom out when transition from list view to map-list
  if (shouldDelayFitBounds) setTimeout(fitBounds, 200);
  else fitBounds();
};
